import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const getDesignCreatedDate = (design) => {
  if (!design?.createdAt) {
    return null;
  }

  if (typeof design.createdAt.toDate === 'function') {
    return design.createdAt.toDate();
  }

  if (design.createdAt.seconds) {
    return new Date(design.createdAt.seconds * 1000);
  }

  if (design.createdAt instanceof Date) {
    return design.createdAt;
  }

  return null;
};

const pickRandomTiedDesign = (designs) => {
  if (!designs.length) {
    return null;
  }

  const topVotes = designs[0].voteCount || 0;
  const tiedDesigns = designs.filter(
    (design) => (design.voteCount || 0) === topVotes
  );

  if (tiedDesigns.length <= 1) {
    return designs[0];
  }

  return tiedDesigns[Math.floor(Math.random() * tiedDesigns.length)];
};

/**
 * Return the highest-voted submitted design for a calendar month.
 * Randomly breaks ties.
 * @param {number} year
 * @param {number} month 1-12
 * @returns {Promise<{ id: string, name: string, designerName: string, imageUrl: string, voteCount: number } | null>}
 */
export const fetchTopDesignForMonth = async (year, month) => {
  const snapshot = await getDocs(collection(db, 'designs'));
  const monthDesigns = [];

  snapshot.forEach((docSnap) => {
    const design = { id: docSnap.id, ...docSnap.data() };
    if (design.status && design.status !== 'submitted') {
      return;
    }

    const createdAt = getDesignCreatedDate(design);
    if (!createdAt) {
      return;
    }

    if (createdAt.getFullYear() !== year || createdAt.getMonth() + 1 !== month) {
      return;
    }

    monthDesigns.push(design);
  });

  if (!monthDesigns.length) {
    return null;
  }

  monthDesigns.sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
  return pickRandomTiedDesign(monthDesigns);
};

/**
 * Aggregate submitted community designs by U.S. state code.
 * @returns {Promise<{ countsByState: Record<string, number>, total: number, statesWithDesigns: number }>}
 */
export const fetchDesignCountsByState = async () => {
  const snapshot = await getDocs(collection(db, 'designs'));
  const countsByState = {};

  snapshot.forEach((docSnap) => {
    const design = docSnap.data();
    if (design.status !== 'submitted' || !design.state) {
      return;
    }

    const stateCode = String(design.state).toUpperCase();
    countsByState[stateCode] = (countsByState[stateCode] || 0) + 1;
  });

  const total = Object.values(countsByState).reduce((sum, count) => sum + count, 0);
  const statesWithDesigns = Object.keys(countsByState).length;

  return { countsByState, total, statesWithDesigns };
};
